import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, AlertCircle, Home, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface CallbackState {
  loading: boolean;
  error: string | null;
  success: boolean;
}

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<CallbackState>({
    loading: true,
    error: null,
    success: false
  });

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get parameters from both hash and query string
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        
        // Check for OAuth errors first (in query params or hash)
        const error = queryParams.get('error') || hashParams.get('error');
        const errorDescription = queryParams.get('error_description') || hashParams.get('error_description');
        
        if (error) {
          console.log('OAuth error detected:', { error, errorDescription });
          
          // Handle different error types
          if (error === 'access_denied') {
            setState({ loading: false, error: null, success: false });
            toast.info('Login cancelled', {
              description: 'You cancelled the login process. No worries!',
              duration: 4000
            });
            
            // Auto-redirect to home after showing message
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 3000);
            
            return;
          } else {
            // Handle other OAuth errors
            setState({ 
              loading: false, 
              error: `Authentication failed: ${errorDescription || error}`, 
              success: false 
            });
            
            toast.error('Authentication Error', {
              description: errorDescription || 'Login failed. Please try again.',
              duration: 5000
            });
            
            return;
          }
        }
        
        // Handle successful OAuth callback (PKCE returns a `code`; implicit returns `access_token`)
        const accessToken = hashParams.get('access_token');
        const authCode = queryParams.get('code') || hashParams.get('code');

        // If session already exists (provider sometimes restores it before we parse URL), just redirect
        const existingSession = await supabase.auth.getSession();
        const persistedRedirect = sessionStorage.getItem('auth_redirect');

        if (existingSession.data.session) {
          setState({ loading: false, error: null, success: true });
          // Prefer explicit redirect query param, then sessionStorage, otherwise home
          const redirectTo = queryParams.get('redirect') || persistedRedirect || '/';
          // Clean up URL and persisted state
          window.history.replaceState(null, '', window.location.pathname);
          sessionStorage.removeItem('auth_redirect');
          toast.success('Successfully signed in!', { description: 'Welcome back to LightsHunt', duration: 3000 });
          setTimeout(() => navigate(redirectTo, { replace: true }), 500);
          return;
        }

        if (authCode) {
          console.log('Exchanging authorization code for session...');
          const { data: { session }, error: exchangeError } = await supabase.auth.exchangeCodeForSession({
            authCode,
          });

          if (exchangeError) throw exchangeError;

          if (session) {
            setState({ loading: false, error: null, success: true });
            window.history.replaceState(null, '', window.location.pathname);
            const redirectTo = queryParams.get('redirect') || persistedRedirect || '/';
            sessionStorage.removeItem('auth_redirect');
            toast.success('Successfully signed in!', {
              description: 'Welcome back to LightsHunt',
              duration: 3000
            });
            setTimeout(() => {
              navigate(redirectTo, { replace: true });
            }, 1000);
            return;
          }
        } else if (accessToken) {
          console.log('Processing successful OAuth callback...');
          
          // Set the session in Supabase
          const { data: { session }, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get('refresh_token') || '',
          });

          if (sessionError) throw sessionError;

          if (session) {
            setState({ loading: false, error: null, success: true });
            
            // Clear the hash and query params from the URL
            window.history.replaceState(null, '', window.location.pathname);
            
            // Check for redirect parameter in the URL or fallback to persisted value
            const redirectTo = queryParams.get('redirect') || persistedRedirect || '/';
            sessionStorage.removeItem('auth_redirect');
            
            toast.success('Successfully signed in!', {
              description: 'Welcome back to LightsHunt',
              duration: 3000
            });
            
            // Redirect to the intended destination
            setTimeout(() => {
              navigate(redirectTo, { replace: true });
            }, 1000);
          }
        } else {
          // No access token and no error - something went wrong
          throw new Error('No access token or authorization code received');
        }
      } catch (error) {
        console.error('Error handling auth callback:', error);
        setState({ 
          loading: false, 
          error: 'Something went wrong during authentication', 
          success: false 
        });
        
        toast.error('Authentication Error', {
          description: 'Something went wrong. Please try logging in again.',
          duration: 5000
        });
      }
    };

    // Set up timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (state.loading) {
        console.warn('Auth callback timeout - redirecting to home');
        setState({ 
          loading: false, 
          error: 'Authentication timeout', 
          success: false 
        });
        
        toast.error('Timeout', {
          description: 'Authentication took too long. Please try again.',
          duration: 4000
        });
        
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      }
    }, 10000); // 10 second timeout

    // Handle the callback if we have URL parameters and user is not authenticated
    if (!isAuthenticated && (window.location.hash || window.location.search)) {
      handleCallback();
    } else if (isAuthenticated) {
      clearTimeout(timeoutId);
      navigate('/', { replace: true });
    } else {
      // No auth parameters, redirect to home
      clearTimeout(timeoutId);
      navigate('/', { replace: true });
    }

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, navigate, state.loading]);

  // Loading state
  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Completing sign in...</h2>
          <p className="text-muted-foreground">Please wait while we redirect you.</p>
        </div>
      </div>
    );
  }

  // Success state
  if (state.success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold mb-2 text-green-600">Success!</h2>
          <p className="text-muted-foreground">Redirecting you now...</p>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Authentication Issue</h2>
        {state.error ? (
          <p className="text-muted-foreground mb-6">{state.error}</p>
        ) : (
          <p className="text-muted-foreground mb-6">
            Your login was cancelled or interrupted. You can try again or return to the homepage.
          </p>
        )}
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            onClick={() => navigate('/', { replace: true })}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Go Home
          </Button>
          <Button 
            onClick={() => navigate('/auth', { replace: true })}
            className="flex items-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}; 